import { Box, Typography } from '@mui/material'
import { ArrowNarrowRightIcon } from '@untitled-theme/icons-react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import Button from '@/shared/components/Button'
import HamburgerMenu from '@/shared/components/HamburgerMenu'

import FundingOverview from '../components/FundingOverview'
import TabToggle from '../components/TabToggle'
import { EnergyCostTab } from '../types'

const EnergyCostOverview = () => {
  const [selectedTab, setSelectedTab] = useState<EnergyCostTab>(EnergyCostTab.Outlook)
  const navigate = useNavigate()
  const { buildingUuid } = useParams<{ buildingUuid: string }>()

  // Lazy load SVG images for better performance
  const [energyPriceDevelopmentSvg, setEnergyPriceDevelopmentSvg] = useState<string>('')
  const [expectedHouseholdCostsSvg, setExpectedHouseholdCostsSvg] = useState<string>('')

  useEffect(() => {
    // Load SVGs only when needed based on selected tab
    if (selectedTab === EnergyCostTab.Outlook) {
      import('../assets/energy-price-development.svg').then(module => {
        setEnergyPriceDevelopmentSvg(module.default)
      })
      import('../assets/expected-household-costs.svg').then(module => {
        setExpectedHouseholdCostsSvg(module.default)
      })
    }
  }, [selectedTab])

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        minHeight: '100vh',
        width: '100vw',
        backgroundColor: '#ffffff',
        padding: { xs: '0 24px' },
      }}
    >
      <HamburgerMenu
        sx={{
          position: 'fixed',
          top: 32,
          left: 32,
          display: { xs: 'flex', lg: 'none' },
        }}
      />

      <Box
        sx={{
          maxWidth: { xs: '100%', lg: '1125px' },
          width: '100%',
          paddingBottom: { xs: '180px', lg: '60px' },
        }}
      >
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            background: 'linear-gradient(180deg, #ffffff 0%, #ffffff 90%, transparent 100%)',
            zIndex: 100,
            paddingTop: { xs: 10, lg: 5 },
            paddingBottom: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: 3,
            }}
          >
            <Box sx={{ display: { xs: 'none', lg: 'flex' } }}>
              <HamburgerMenu />
            </Box>
            <Typography
              component="h1"
              sx={{
                fontWeight: 600,
                fontSize: '28px',
                lineHeight: '42px',
                marginLeft: '8px',
              }}
            >
              <Box component="span" sx={{ color: '#5BC06B' }}>
                Ausblick
              </Box>{' '}
              <Box component="span">Energiekosten</Box>
            </Typography>
          </Box>

          <TabToggle selectedTab={selectedTab} onTabChange={setSelectedTab} />
        </Box>

        {/* Content for each tab */}
        {selectedTab === EnergyCostTab.Outlook && (
          <Box sx={{ marginTop: 1, maxWidth: { xs: '100%', lg: '800px' }, marginX: 'auto' }}>
            {energyPriceDevelopmentSvg && (
              <Box
                component="img"
                src={energyPriceDevelopmentSvg}
                alt="Energy Price Development"
                sx={{ width: '100%', marginBottom: '18px' }}
              />
            )}
            {expectedHouseholdCostsSvg && (
              <Box
                component="img"
                src={expectedHouseholdCostsSvg}
                alt="Expected Household Costs"
                sx={{ width: '100%' }}
              />
            )}
          </Box>
        )}

        {selectedTab === EnergyCostTab.Funding && (
          <Box sx={{ marginTop: 1, maxWidth: { xs: '100%', lg: '800px' }, marginX: 'auto' }}>
            <FundingOverview />
          </Box>
        )}
      </Box>

      <Button
        endIcon={<ArrowNarrowRightIcon />}
        onClick={() => navigate(`/${buildingUuid}/current-rating`)}
        sx={{
          position: 'fixed',
          bottom: { xs: 24, lg: 32 },
          right: { xs: 24, lg: 32 },
        }}
      >
        Weiter
      </Button>
    </Box>
  )
}

export default EnergyCostOverview
